import { NgClass, NgStyle } from '@angular/common';
import { Component, inject, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { ReactiveFormsModule } from '@angular/forms';
import { Store } from '@ngrx/store';
import { TranslatePipe } from '@ngx-translate/core';
import { ButtonModule } from '@openng/optimus-ui/button';
import { RadioButtonModule } from '@openng/optimus-ui/radiobutton';
import { RippleModule } from '@openng/optimus-ui/ripple';
import { LanguageEnum } from '../../../shared/models/language.enum';
import { AppConfig, LayoutService } from '../../services/layout.service';
import { MenuService } from '../../services/menu.service';
import { ThemeService } from '../../services/theme.service';
import { changeLanguage, changeUserSettings } from '../../store/layout.actions';
import { LayoutState } from '../../store/layout.state';
import { ButtonComponent } from '../../../../components/kit/button/button.component';
import { Tooltip } from '@openng/optimus-ui/tooltip';
import { defaultMenuThemes } from '../../../content/service/types/user.types';
import { AppFormGroup } from '../../../../utils/form.utils';
import { fb } from '../../../shared/shared.global-service';
import { BaseDialogComponent } from '../../../../components/kit/base-dialog/base-dialog.component';
import { RadioInputComponent } from '../../../../components/kit/input/radio-input/radio-input.component';
import { selectUserSettings } from '../../store/layout.selectors';

interface ThemeItem {
    name: string;
    color: string;
}

export interface MenuThemeItem extends ThemeItem {
    logoColor: string;
}

@Component({
    selector: 'app-config',
    templateUrl: './config.component.html',
    styleUrls: ['./config.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        NgClass,
        NgStyle,
        RippleModule,
        RadioButtonModule,
        ButtonModule,
        TranslatePipe,
        ButtonComponent,
        Tooltip,
        ReactiveFormsModule,
        RadioInputComponent,
    ],
})
export class ConfigComponent extends BaseDialogComponent implements OnInit {

    public readonly layoutService = inject(LayoutService);
    public readonly menuService = inject(MenuService);
    public readonly store = inject(Store<LayoutState>);
    public readonly themeService = inject(ThemeService);

    protected languages = [
        { name: 'česky', code: LanguageEnum.CZECH },
        { name: 'english', code: LanguageEnum.ENGLISH },
    ];
    protected colorSchemas = [
        { name: 'light', value: 'light' },
        { name: 'dark', value: 'dark' },
    ];
    protected menuTypes = [
        { name: 'static', value: 'static' },
        { name: 'overlay', value: 'overlay' },
        { name: 'slim', value: 'slim' },
        { name: 'compact', value: 'compact' },
        { name: 'horizontal', value: 'horizontal' },
    ];
    protected componentThemes: ThemeItem[] = [
        { name: 'emerald', color: 'var(--p-emerald-500)' },
        { name: 'green', color: 'var(--p-green-500)' },
        { name: 'lime', color: 'var(--p-lime-500)' },
        { name: 'red', color: 'var(--p-red-500)' },
        { name: 'orange', color: 'var(--p-orange-500)' },
        { name: 'amber', color: 'var(--p-amber-500)' },
        { name: 'yellow', color: 'var(--p-yellow-500)' },
        { name: 'teal', color: 'var(--p-teal-500)' },
        { name: 'cyan', color: 'var(--p-cyan-500)' },
        { name: 'sky', color: 'var(--p-sky-500)' },
        { name: 'blue', color: 'var(--p-blue-500)' },
        { name: 'indigo', color: 'var(--p-indigo-500)' },
        { name: 'violet', color: 'var(--p-violet-500)' },
        { name: 'purple', color: 'var(--p-purple-500)' },
        { name: 'fuchsia', color: 'var(--p-fuchsia-500)' },
        { name: 'pink', color: 'var(--p-pink-500)' },
        { name: 'rose', color: 'var(--p-rose-500)' },
    ];
    protected surfaces: ThemeItem[] = [
        { name: 'slate', color: 'var(--p-slate-500)' },
        { name: 'gray', color: 'var(--p-gray-500)' },
        { name: 'zinc', color: 'var(--p-zinc-500)' },
        { name: 'neutral', color: 'var(--p-neutral-500)' },
        { name: 'stone', color: 'var(--p-stone-500)' },
    ];
    protected menuThemes: MenuThemeItem[] = defaultMenuThemes;
    protected scales: number[] = [12, 13, 14, 15, 16, 18, 20, 22, 24, 26];

    protected form = new AppFormGroup({
        language: fb.control<string | null>(null),
        colorScheme: fb.control<string | null>(null),
        menuType: fb.control<string | null>(null),
        menuTheme: fb.control<string | null>(null),
        componentTheme: fb.control<string | null>(null),
        surface: fb.control<string | null>(null),
        scale: fb.control<number | null>(null),
    });

    constructor() {
        super();
    }

    ngOnInit(): void {
        this.form.patchValue(this.store.selectSignal(selectUserSettings)());

        this.subs.push(
            this.form.valueChanges.subscribe((settings) => {
                if (settings) {
                    this.layoutService.config.set(settings as AppConfig);
                    this.changeUserConfigSettings(this.layoutService.config());
                    this.themeService.setTheme(this.layoutService.config());
                }
            }),
            this.form.controls.language.valueChanges.subscribe((language) => {
                this.store.dispatch(changeLanguage({ language: language || LanguageEnum.CZECH }));
            }),
            this.form.controls.menuType.valueChanges.subscribe(() => {
                this.menuService.reset();
            }),
        );
    }

    changeUserConfigSettings(value: AppConfig): void {
        this.store.dispatch(
            changeUserSettings({
                input: {
                    ...value,
                    userId: '',
                },
            }),
        );
    }

    changeComponentTheme(componentTheme: string): void {
        this.form.patchValue({ componentTheme });
    }

    changeComponentSurface(surface: string): void {
        this.form.patchValue({ surface });
    }

    changeMenuTheme(menuTheme: string): void {
        this.form.patchValue({ menuTheme });
    }

    decrementScale(): void {
        const idx = this.scales.findIndex((scale) => scale === this.form.value.scale);
        if (idx > 0) {
            this.form.patchValue({ scale: this.scales[idx - 1] });
        }
    }

    incrementScale(): void {
        const idx = this.scales.findIndex((scale) => scale === this.form.value.scale);
        if (idx !== -1 && idx + 1 < this.scales.length) {
            this.form.patchValue({ scale: this.scales[idx + 1] });
        }
    }
}
